// The sections and their option forms, rendered from the configuration answer (the tenant's report
// profile): no file here names a section or an option. A stored section the catalogue no longer offers
// stays until the user removes it; unknown option keys stay as they came.
import React from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
} from "@mui/material";
import { fieldError } from "utils/reportErrors";

function OptionField({ name, opt, value, onChange, error }) {
  const current = value !== undefined ? value : opt.default;
  const label = opt.unit ? `${opt.label || name} (${opt.unit})` : opt.label || name;
  const help = error || opt.help || " ";
  if (opt.type === "boolean") {
    return (
      <FormControl error={!!error}>
        <FormControlLabel
          control={<Checkbox size="small" color="secondary" checked={!!current} onChange={(e) => onChange(e.target.checked)} />}
          label={<span style={{ fontSize: "14px" }}>{label}</span>}
        />
        {help.trim() ? <FormHelperText style={{ marginTop: 0 }}>{help}</FormHelperText> : null}
      </FormControl>
    );
  }
  if (opt.type === "number") {
    return (
      <TextField
        size="small"
        type="number"
        label={label}
        value={current === undefined || current === null ? "" : current}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        error={!!error}
        helperText={help}
        inputProps={{ min: opt.min, max: opt.max }}
        style={{ width: "240px" }}
      />
    );
  }
  if (opt.type === "multiselect") {
    return (
      <FormControl size="small" error={!!error} style={{ width: "320px" }}>
        <InputLabel>{label}</InputLabel>
        <Select
          multiple
          value={Array.isArray(current) ? current : []}
          onChange={(e) => onChange(e.target.value)}
          input={<OutlinedInput label={label} />}
          renderValue={(picked) => picked.join(", ")}
        >
          {(opt.values || []).map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{help}</FormHelperText>
      </FormControl>
    );
  }
  return null;
}

export default function SectionPicker({ sections, catalogue, onChange, errors }) {
  const toggle = (key, on) =>
    onChange(on ? [...sections, { key, options: {} }] : sections.filter((s) => s.key !== key));
  const setOption = (key, name, value) =>
    onChange(sections.map((s) => (s.key === key ? { ...s, options: { ...(s.options || {}), [name]: value } } : s)));
  const retired = sections.filter((s) => !catalogue.some((c) => c.key === s.key));

  return (
    <div>
      {catalogue.length ? null : <p style={{ fontSize: "14px", color: "#c7c7c7", margin: 0 }}>No sections are offered here yet.</p>}
      {catalogue.map((section) => {
        const index = sections.findIndex((s) => s.key === section.key);
        const options = Object.entries(section.optionsSchema || {});
        return (
          <div key={section.key}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  color="secondary"
                  checked={index >= 0}
                  onChange={(e) => toggle(section.key, e.target.checked)}
                />
              }
              label={<span style={{ fontSize: "14px" }}>{section.title || section.key}</span>}
            />
            {index >= 0 && options.length ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px", margin: "8px 0 8px 30px" }}>
                {options.map(([name, opt]) => (
                  <OptionField
                    key={name}
                    name={name}
                    opt={opt}
                    value={(sections[index].options || {})[name]}
                    onChange={(value) => setOption(section.key, name, value)}
                    error={fieldError(errors, `sections.${index}.options.${name}`)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        );
      })}
      {retired.map((s) => (
        <FormControlLabel
          key={s.key}
          control={<Checkbox size="small" color="secondary" checked onChange={() => toggle(s.key, false)} />}
          label={<span style={{ fontSize: "14px", color: "grey" }}>{s.key} (no longer available; untick to remove)</span>}
        />
      ))}
      {errors && errors.sections ? <p style={{ fontSize: "12px", color: "#bf3535", margin: "4px 0 0 0" }}>{errors.sections}</p> : null}
    </div>
  );
}
